import type {
  Catalog,
  ConnectorMeta,
  QueryResult,
  UserView,
} from "./types";

/**
 * HTTP access layer.
 *
 * This is the one module the desktop build does not share: there, every call
 * went through a Tauri command that forwarded to the same endpoints from Rust.
 * Here the browser talks to the service directly — one hop instead of three,
 * and one JSON parse instead of two.
 *
 * ## Where requests go
 *
 * Always same-origin, via the `/api` prefix. In development Vite proxies it to
 * `hle_server`; in production the reverse proxy that terminates TLS does the
 * same. Same-origin is not incidental — it is what lets the session cookie work
 * without CORS credential special-casing, and it keeps dev honest about prod.
 */
const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "/api";

/**
 * Whether the API lives on a different origin than this page.
 *
 * Decides the fetch credentials mode, and getting it wrong fails in opposite
 * directions: `same-origin` against a foreign API sends no cookie at all (every
 * call 401s), while `include` requires the server to answer with an explicit
 * `Access-Control-Allow-Credentials` and a named origin — a wildcard is
 * rejected by the browser.
 *
 * Derived from the configured base rather than a separate flag, so the two
 * cannot disagree: an absolute URL IS the cross-origin case.
 */
const CROSS_ORIGIN = /^https?:\/\//i.test(BASE);

/** Cookies must be sent either way; only the mode differs. */
const CREDENTIALS: RequestCredentials = CROSS_ORIGIN ? "include" : "same-origin";

/** Called when the server rejects our session; the shell shows the login screen. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

/** An error carrying the HTTP status, so callers can tell 404 from 500. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Response is a file, not JSON. */
  raw?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? "GET",
    // Send the session cookie. Without this fetch omits credentials even
    // same-origin for cross-site request modes, and every call would 401.
    credentials: CREDENTIALS,
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(`${BASE}${path}`, init);

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError("Session expired — sign in again.", 401);
  }

  if (!res.ok) {
    throw new ApiError(await errorMessage(res), res.status);
  }

  if (opts.raw) return (await res.blob()) as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Best-effort human-readable message from a failed response.
 *
 * The service answers with a plain-text message on most errors and JSON on a
 * few, so try both before falling back to the status code — an operator reading
 * "connector not found" is better served than one reading "HTTP 404".
 */
async function errorMessage(res: Response): Promise<string> {
  try {
    const text = await res.text();
    if (!text) return `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      return parsed.message ?? parsed.error ?? text;
    } catch {
      return text;
    }
  } catch {
    return `HTTP ${res.status}`;
  }
}

// ── Auth ─────────────────────────────────────────────────────────────────────
// The session token is never touched by JavaScript. The server sets it as an
// HttpOnly cookie, which means a cross-site script cannot read it — unlike
// localStorage, where one XSS exfiltrates every logged-in session.

export function login(email: string, password: string): Promise<UserView> {
  return request<UserView>("/auth/login", {
    method: "POST",
    body: { email, password },
  });
}

export function logout(): Promise<void> {
  return request<void>("/auth/logout", { method: "POST" });
}

/** Resolve the current session at boot. `null` when signed out. */
export async function currentUser(): Promise<UserView | null> {
  try {
    return await request<UserView>("/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

// ── Connectors & catalog ─────────────────────────────────────────────────────

export function listConnections(): Promise<ConnectorMeta[]> {
  return request<ConnectorMeta[]>("/connectors");
}

export function getCatalog(connId: string): Promise<Catalog> {
  return request<Catalog>(`/connectors/${encodeURIComponent(connId)}/catalog`);
}

// ── Queries ──────────────────────────────────────────────────────────────────

export function runQuery(
  connectorId: string,
  sql: string,
  signal?: AbortSignal,
): Promise<QueryResult> {
  return request<QueryResult>("/query", {
    method: "POST",
    body: { connectorId, sql },
    signal,
  });
}

/** One widget's slot in a batch response. */
export interface BatchResult {
  id: string;
  result?: QueryResult;
  error?: string;
  cached: boolean;
}

/**
 * Run every widget query on a page in ONE request.
 *
 * A dashboard page is one query per widget; issuing them individually costs a
 * round trip, a connector lookup, a secret decrypt and an audit write each. The
 * server also dedupes identical SQL and runs the rest concurrently, so the page
 * costs one wait rather than the sum of them.
 */
export async function runQueryBatch(
  connectorId: string,
  queries: { id: string; sql: string }[],
  signal?: AbortSignal,
): Promise<BatchResult[]> {
  const res = await request<{ results: BatchResult[] }>("/query/batch", {
    method: "POST",
    body: { connectorId, queries },
    signal,
  });
  return res.results;
}

// ── Export ───────────────────────────────────────────────────────────────────

/**
 * Download one table's FULL result as .xlsx or .csv.
 *
 * The server gates this at its export row cap rather than the screen cap, so a
 * table showing the first 100,000 of 823,286 rows still exports all of them.
 *
 * Two delivery paths, and the difference matters at this size:
 *
 *  * **File System Access API** — streams straight to the chosen file, so the
 *    browser never holds the whole export in memory. Chromium-only and needs a
 *    secure context (HTTPS or localhost).
 *  * **Blob + `<a download>`** — works everywhere, but buffers the entire file
 *    in memory first; Chromium's practical ceiling is around 500 MB.
 *
 * Returns `false` when the user cancelled the save dialog.
 */
export async function exportTable(
  connectorId: string,
  sql: string,
  format: "xlsx" | "csv",
  title: string,
): Promise<boolean> {
  const filename = `${safeFilename(title)}.${format}`;

  // Ask for the destination BEFORE fetching: the picker must be called from the
  // user's click, and a long download in between loses that user activation.
  let handle: FileSystemFileHandle | undefined;
  if (canStreamToDisk()) {
    try {
      handle = await window.showSaveFilePicker!({
        suggestedName: filename,
        types: [
          format === "xlsx"
            ? {
                description: "Excel workbook",
                accept: {
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                },
              }
            : { description: "CSV", accept: { "text/csv": [".csv"] } },
        ],
      });
    } catch (e) {
      // `AbortError` means two very different things here, and treating them
      // alike is a real bug: a user pressing Cancel should end the export, but
      // an environment that REFUSES to show the picker at all (an automation
      // harness intercepting it, an embedded webview, a policy block) must fall
      // back to the blob download instead of silently doing nothing.
      //
      // The two are distinguishable only by the message, so the default is the
      // safe direction: fall back, and treat it as a cancel only when the
      // browser says the user did the aborting.
      if (isUserCancel(e)) return false;
      handle = undefined;
    }
  }

  const res = await fetch(`${BASE}/query/export`, {
    method: "POST",
    credentials: CREDENTIALS,
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ connectorId, sql, format, title }),
  });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError("Session expired — sign in again.", 401);
  }
  if (!res.ok) throw new ApiError(await errorMessage(res), res.status);

  if (handle && res.body) {
    // Stream: bytes go from the network to the file without a full copy in the
    // tab's memory.
    const writable = await handle.createWritable();
    await res.body.pipeTo(writable);
    return true;
  }

  const blob = await res.blob();
  triggerDownload(blob, filename);
  return true;
}

function canStreamToDisk(): boolean {
  // `isSecureContext` matters: the API exists on the object but throws outside
  // HTTPS/localhost, and we would rather choose the fallback than catch that.
  return typeof window.showSaveFilePicker === "function" && window.isSecureContext;
}

/**
 * Did the USER dismiss the save dialog, as opposed to the environment refusing
 * to show it?
 *
 * Both surface as `AbortError`. Chromium words a genuine dismissal as "The user
 * aborted a request" / "user activation is required"; an interception says so
 * ("Intercepted by Page.setInterceptFileChooserDialog"). Matching on the user
 * wording — rather than on `AbortError` alone — means an unrecognised refusal
 * falls back to the blob download instead of being mistaken for a cancel.
 */
function isUserCancel(e: unknown): boolean {
  if (!(e instanceof DOMException) || e.name !== "AbortError") return false;
  return /\buser\b/i.test(e.message);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers; one turn of
  // the event loop is enough for it to have started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Make a widget title safe to propose as a filename. */
function safeFilename(raw: string): string {
  const cleaned = raw.replace(/[/\\:*?"<>|]/g, "-").replace(/\s+/g, " ").trim();
  return cleaned.slice(0, 120) || "export";
}

/**
 * Internals exposed for unit tests.
 *
 * These two decide, respectively, whether a failed save dialog means "the user
 * said no" (stop) or "this environment cannot show one" (fall back), and what
 * filename to propose. Both are pure and both had bugs worth pinning down.
 */
export const __testing = { isUserCancel, safeFilename };
